/**
 * ProjectSwitcher — Interlock topbar project selector (multi-project, Nick
 * 2026-07-19, decision D1). Drives the runtime `PROJECT_ID` in interlockApi.
 *
 * Deliberately renders NOTHING when there is 0-or-1 project — so a single-
 * project deployment (today: only PRO-302) sees no change at all. It lights up
 * automatically the moment a second project exists.
 *
 * A switch persists the choice (setActiveProjectId → localStorage) and does a
 * full page reload, so the whole Interlock tree re-initialises cleanly with the
 * new project — no stale react-query cache, no partial re-scope. A project
 * switch is an infrequent, deliberate action, so a reload is the right trade
 * for correctness + simplicity.
 */
import { useProjects } from '../../hooks/useProjects';
import { getActiveProjectId, setActiveProjectId } from '../../api/interlockApi';

export function ProjectSwitcher() {
  const { data: projects = [] } = useProjects();

  // Zero visible/behavioural change on a single-project deployment.
  if (projects.length <= 1) return null;

  const current = getActiveProjectId();
  const onChange = (id: string) => {
    if (id === current) return;
    setActiveProjectId(id);
    // Full reload so every page + query re-scopes to the new project cleanly.
    window.location.reload();
  };

  return (
    <label className="ilk-project-switcher" title="Active project">
      <span className="ilk-project-switcher__label">Project</span>
      <select
        className="ilk-project-switcher__select"
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.celoxisProjectNumber ? `${p.celoxisProjectNumber} — ${p.name}` : p.name}
          </option>
        ))}
      </select>
    </label>
  );
}
